import { useEffect, type RefObject } from 'react';

/**
 * Marks a scroll container with `data-fade-start` / `data-fade-end` so CSS can
 * fade ONLY the edge that still has content behind it.
 *
 * WHY THIS EXISTS. Every list in the shell is taller than its box at some
 * width, so a row is always sliced flat against one edge — the owner read it as
 * cards being "cut off from below" (2026-09-07). No scroll position avoids it:
 * with content taller than the box, resting at the top slices the bottom row
 * and resting at the bottom slices the top one, and only an exact multiple of
 * the row height would ever land clean. A slice that FADES reads as "this
 * continues"; a slice with a hard edge reads as a rendering fault.
 *
 * WHY IT IS CONDITIONAL rather than a static mask on both edges. A permanent
 * top fade dims the first row while the list is already at the top, which looks
 * like the bug it was meant to fix. The tab row solves the same problem with
 * padding equal to the fade, but that only works because horizontal room there
 * is free — here it would add 2x the fade to every list's content height and
 * push shelves that currently fit into scrolling.
 *
 * WHY NOT CSS ALONE. `animation-timeline: scroll()` is Chromium-only, and the
 * `background-attachment: local` shadow trick needs the scroller to paint its
 * own opaque background, which these do not — they sit transparent over the
 * pane so the theme tokens show through.
 */

const FADE_EPS = 2; // sub-pixel scroll positions must still count as "at the end"

/* Prefixed attribute names, so the CSS can select on the attribute's mere
   PRESENCE — being wired to this hook is the whole condition for the mask, and
   nothing else in the app writes them. */
function apply(el: HTMLElement | null) {
  if (!el) return;
  const max = el.scrollHeight - el.clientHeight;
  el.dataset.pnFadeStart = String(el.scrollTop > FADE_EPS);
  el.dataset.pnFadeEnd = String(max > FADE_EPS && el.scrollTop < max - FADE_EPS);
}

export function useScrollFade(ref: RefObject<HTMLElement | null>) {
  /* Scroll and resize cover the visitor's own scrolling and every breakpoint
     change. Children are observed too: a grid growing by a row changes what is
     hidden without the container itself resizing. */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => apply(el);
    el.addEventListener('scroll', update, { passive: true });
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
      for (const child of Array.from(el.children)) ro.observe(child);
    }
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
    };
  }, [ref]);

  /* And after EVERY render, deliberately without a dependency list. The scripted
     sequences change content by appending nodes — a section lands, the new card
     arrives — which neither scrolls the container nor resizes any box the
     observer above is watching, but always comes with a render. */
  useEffect(() => {
    apply(ref.current);
  });
}
